/**
 * Confirm permanently deleting a review session and all data in it.
 */

import { useState } from "react";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import { agentReviewAPI, type DeleteSessionResult } from "@/services/api/agentReview";

export interface DeleteSessionDialogProps {
  open: boolean;
  sessionId: string | null;
  sessionName: string;
  taskTypeLabel: string;
  onClose: () => void;
  onDeleted: (result: DeleteSessionResult) => void;
}

export function DeleteSessionDialog({
  open,
  sessionId,
  sessionName,
  taskTypeLabel,
  onClose,
  onDeleted,
}: DeleteSessionDialogProps) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!sessionId) return;
    setDeleting(true);
    setError(null);
    try {
      const result = await agentReviewAPI.deleteReviewSession(sessionId);
      onDeleted(result);
      onClose();
    } catch {
      setError("Couldn't delete that session. Try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="sm">
      <DialogTitle>Delete review session</DialogTitle>
      <DialogBody className="space-y-3">
        <p className="text-sm text-slate-300 leading-relaxed">
          Permanently delete{" "}
          <span className="text-slate-100">{sessionName}</span> from{" "}
          <span className="text-slate-100">{taskTypeLabel}</span>?
        </p>
        <p className="text-sm text-slate-400 leading-relaxed">
          All queued cases, submitted reviews, Ace ratings, and version history in this session
          will be removed. Production agent runs are not affected.
        </p>
        {error ? <p className="text-sm text-rose-400">{error}</p> : null}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          color="red"
          disabled={deleting || !sessionId}
          data-testid="agent-review-delete-session-confirm"
          onClick={() => void handleDelete()}
        >
          {deleting ? "Deleting…" : "Delete session"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
